import React from 'react';

// Shared style baseline for every toolbar button — focus ring, min
// tap-target size, transition timing. Disabled treatment is layered
// in per-variant because the two palettes want different disabled
// visuals (default = grey chip + dimmed contents, ghost = unchanged
// background + lightened icon/text only).
//
// The focus ring uses `focus-visible:` (not `focus:`) so it only
// appears for keyboard navigation — mouse clicks no longer light
// up a yellow halo around the button. We also use a neutral grey
// ring instead of yellow so the keyboard outline doesn't clash with
// the otherwise dark / ghost toolbar palette.
const TOOLBAR_BTN_BASE_CLASSES = `
  focus:outline-none
  focus-visible:ring-2 focus-visible:ring-gray-400/60
  focus-visible:outline-none
  min-h-[44px] min-w-[44px] cursor-pointer rounded text-gray-800
  transition-transform duration-100 active:scale-95
  disabled:cursor-not-allowed disabled:active:scale-100
`;

// "Resting" palette — bg-yellow-500 with hover/active flashes. When
// disabled the whole chip turns neutral grey and dims so the
// disabled state reads as "off" against the busy toolbar chrome.
const TOOLBAR_BTN_PALETTE_DEFAULT = `
  bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700
  disabled:bg-gray-400 disabled:opacity-50 disabled:hover:bg-gray-400
  disabled:active:bg-gray-400
`;

// Pressed palette for toggle buttons (Pitch / Accidental). The
// pressed state is the resting state so we drop the hover/active
// flashes; `active:scale-95` from the base still provides tactile
// feedback on tap.
const TOOLBAR_BTN_PALETTE_PRESSED = 'bg-yellow-600';

// Ghost palette — transparent bg with a soft hover wash. Used by the
// new Figma toolbar where the chrome itself is white and individual
// buttons should read as icon-only glyphs rather than coloured chips.
//
// Open-popover treatment: when the button drives a dropdown/popover
// and the popover is open, the caller sets `aria-expanded="true"`.
// The `aria-expanded:bg-gray-200` variant picks that up and keeps
// the button slightly darker (same shade as `:active`) for the whole
// time the popover is showing — replaces the old yellow focus ring
// as the "this button is currently driving a panel" cue.
//
// Disabled treatment: deliberately KEEPS the transparent background
// (no grey fill) and KEEPS the resting opacity. Instead we just
// fade the text colour so the contents look "unavailable" while the
// button outline doesn't flash a heavy grey chip — matches the
// Figma edit-toolbar pattern where Reset / Undo just dim their
// glyph + label when there's nothing left to undo.
const TOOLBAR_BTN_PALETTE_GHOST = `
  bg-transparent text-gray-700 hover:bg-gray-100 active:bg-gray-200
  aria-expanded:bg-gray-200 aria-expanded:hover:bg-gray-200
  disabled:bg-transparent disabled:text-gray-400
  disabled:hover:bg-transparent disabled:active:bg-transparent
`;

const TOOLBAR_BTN_LAYOUTS = {
  // Square icon-only button (Undo, Redo). Flex-centered so the glyph
  // sits in the middle of the 44px min-tap-target instead of hugging
  // the top-left like an inline-block <img> would by default.
  icon: 'flex items-center justify-center p-2',
  // Icon + label button (Save, Download PDF, Download XML, Pitch,
  // Accidental, Fingering Size).
  iconLabel: 'flex items-center gap-x-2 px-4 py-2',
  // Label-only button (Show/Hide Alternative Fingerings). Stays
  // block so the long label can wrap naturally.
  label: 'px-4 py-2',
} as const;

export type ToolbarButtonLayout = keyof typeof TOOLBAR_BTN_LAYOUTS;

export type ToolbarButtonVariant = 'default' | 'ghost';

export interface ToolbarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  layout?: ToolbarButtonLayout;
  /** When defined, sets `aria-pressed` and swaps the palette to the
   *  pressed variant. Leave undefined for non-toggle buttons. */
  pressed?: boolean;
  /** Visual palette. `default` is the legacy yellow chip used by the
   *  Mxml project page; `ghost` is the transparent treatment used by
   *  the new white Figma toolbar on the PDF project page. */
  variant?: ToolbarButtonVariant;
}

export const ToolbarButton = React.forwardRef<
  HTMLButtonElement,
  ToolbarButtonProps
>(function ToolbarButton(
  {
    layout = 'iconLabel',
    pressed,
    variant = 'default',
    className,
    children,
    ...rest
  },
  ref
) {
  let palette: string;
  if (variant === 'ghost') {
    palette = TOOLBAR_BTN_PALETTE_GHOST;
  } else if (pressed) {
    palette = TOOLBAR_BTN_PALETTE_PRESSED;
  } else {
    palette = TOOLBAR_BTN_PALETTE_DEFAULT;
  }
  return (
    <button
      ref={ref}
      aria-pressed={pressed}
      className={`
        ${TOOLBAR_BTN_BASE_CLASSES}
        ${palette}
        ${TOOLBAR_BTN_LAYOUTS[layout]}
        ${className ?? ''}
      `}
      {...rest}
    >
      {children}
    </button>
  );
});
